import { v4 as uuid } from 'uuid';
import {
    ADD_DISCOUNT_MUTATION,
    ADD_GIFT_PRODUCT_TO_ORDER_MUTATION,
    ADD_GIFT_RECIPIENT_MUTATION,
    ADD_OPTIONS_TO_ORDER_PRODUCT_MUTATION,
    ADD_PRODUCT_TO_ORDER_MUTATION,
    ADD_PRODUCT_TO_ORDER_MUTATION_V2,
    ADD_PRODUCT_TO_UPSELL_FEATURED_MUTATION,
    COMPLETE_ORDER_MUTATION,
    COMPLETE_ORDER_MUTATION_V2,
    CREATE_CART_MUTATION,
    CREATE_ORDER_CONTACT_MUTATION,
    DRIVER_LOCATION_QUERY,
    EDIT_ORDER_CONTACT_DETAILS_MUTATION,
    GENERATE_ORDER_OTP_MUTATION,
    GET_CANCEL_REASONS,
    ORDER_CANCEL,
    ORDER_CONFIRMATION_QUERY,
    ORDER_ESTIMATE_QUERY,
    ORDER_EVENT,
    ORDER_PRODUCT_OOS_QUERY,
    ORDER_QUERY,
    ORDER_QUERY_V2,
    ORDER_TRACKER_QUERY,
    ORDER_USE_COOKIE_DOUGH_MUTATION,
    REMOVE_CONTACT_DETAILS_MUTATION,
    REMOVE_DISCOUNT_MUTATION,
    REMOVE_ITEM_FROM_ORDER_MUTATION,
    SET_DELIVERY_TIME_MUTATION,
    SET_ORDER_SHIPPING_ADDRESS,
    SET_ORDER_SHIPPING_METHOD,
    SET_ORDER_TIP_MUTATION,
    SET_PAYMENT_METHOD_MUTATION,
    UPDATE_GIFT_PRODUCT_TO_ORDER_MUTATION,
    UPDATE_ORDER_COMMENT,
    UPDATE_ORDER_DETAILS,
    UPDATE_ORDER_INCLUDESPOON,
    UPDATE_ORDER_LOCATION,
    UPDATE_PRODUCT_ORDER_MUTATION,
    VIEW_ORDER_DETAILS_QUERY,
} from '../../../graphql/order';

import {
    setPreviousBigOrderWarningId,
    showBigOrderWarning,
} from "../../../redux/actions/alert";
import { addOptimisticItem, clearOptimisticItems } from '../../../redux/actions/optimisticCart';
import { orderUpdated } from "../../../redux/actions/order";
import * as elementsActions from "../../../redux/actions/elements";
import { normalizeLocationAccessStoreId } from "../../utilities/locationAccessAcknowledgement";
import { setCartTransactionId } from '../../../redux/actions/user';
import { store } from "../../../redux/store";
import { client } from "../../utilities/config";
import amplitudeAnalytics from '../../utilities/amplitude';

/**
 * Updates the order redux store with updated order info
 *
 * @param {Promise} promise
 * @param {Array<String>} resultPath
 * @param checkForBigOrderWarning
 */
function updateOrder(promise, resultPath, checkForBigOrderWarning = false) {
    promise.then((result) => {

        let { data } = result;
        const { order, alert } = store.getState();

        resultPath.forEach((path) => {
            data = data[path];
        });

        const newOrder = {...order, ...data};

        const prevStoreId = normalizeLocationAccessStoreId(order?.store?.id);
        const nextStoreId = normalizeLocationAccessStoreId(newOrder?.store?.id);
        if (
          prevStoreId != null &&
          nextStoreId != null &&
          prevStoreId !== nextStoreId
        ) {
          const acknowledgedStoreId = normalizeLocationAccessStoreId(
            store.getState().elements?.locationAccessAcknowledgedStoreId
          );
          if (acknowledgedStoreId !== nextStoreId) {
            store.dispatch(
              elementsActions.setLocationAccessAcknowledgedStoreId(null)
            );
          }
        }

        if (checkForBigOrderWarning && newOrder.isBigOrder) {
          const previousWarningOrderId = alert.previousBigOrderWarningId;
          if (previousWarningOrderId !== newOrder.id) {
            store.dispatch(showBigOrderWarning());
            store.dispatch(setPreviousBigOrderWarningId(newOrder.id));
          }
        }

        store.dispatch(orderUpdated(newOrder));
    }).catch(() => {});
}

export const removeProducts = async (orderCode, orderProductIds) => {
    const promise = client.mutate({
        mutation: REMOVE_ITEM_FROM_ORDER_MUTATION,
        variables: {
            orderCode,
            data: orderProductIds
        }
    });

    updateOrder(promise, ['removeOrderItemsFromOrder']);

    return promise;
};

/**
 * Updates the order with a new location/address
 *
 * @param {Object} data {
 *     orderCode: String!,
 *     orderTypeId: Int,
 *     storeId: ID,
 *     address: {
 *         address1: String!,
 *         city: String!,
 *         state: String!,
 *         postcode: String!
 *         lat: Float!
 *         lng: Float!
 *     },
 *     shippingMethod: ID,
 * }
 * @returns {Promise<FetchResult<any>>}
 */
export async function updateOrderLocation(orderCode, data) {

    const promise = client.mutate(
        {
            mutation: UPDATE_ORDER_LOCATION,
            variables: {
                orderCode,
                data
            }
        }
    );

    updateOrder(promise, ['updateOrder']);
    return promise;
}

export function addGiftcardOrPromotion(orderCode, couponIds, orderParams, gRepcaptchaResponse = null) {
    const promise = client.mutate(
        {
            mutation: ADD_DISCOUNT_MUTATION,
            variables: {
                orderCode,
                couponIds,
                orderParams,
                gRepcaptchaResponse
            }
        }
    );

    updateOrder(promise, ['addDiscountToOrder']);

    return promise;
}

export function getOrder(orderCode, orderParams, query = ORDER_QUERY, context = undefined) {
    const promise = client.query(
        {
            fetchPolicy: 'no-cache',
            query,
            context,
            variables: {
                orderCode,
                orderParams
            }
        }
    );

    updateOrder(promise, ['order'])

    return promise;
}


export function getOrderWithoutUpdate(orderCode, orderParams, query = ORDER_QUERY, context = undefined) {
    const promise = client.query(
        {
            fetchPolicy: 'no-cache',
            // Keep partial order payload when nested fields (e.g. storePaymentMethods) error,
            // so Order Details is not stuck on the loader.
            errorPolicy: 'all',
            query,
            context,
            variables: {
                orderCode,
                orderParams
            }
        }
    );

    return promise;
}

export function getOrderEstimate(orderCode) {
    const promise = client.query(
        {
            fetchPolicy: 'no-cache',
            query: ORDER_ESTIMATE_QUERY,
            variables: {
                orderCode,
            }
        }
    );

    updateOrder(promise, [])

    return promise;
}

export const orderProductsAvailability= (orderCode) => {
    return client.query({
        fetchPolicy: 'no-cache',
        query: ORDER_PRODUCT_OOS_QUERY,
        variables: {
            orderCode
        }
    })
}

export function removeDiscount(orderCode, couponIds, orderParams) {
    const promise = client.mutate(
        {
            mutation: REMOVE_DISCOUNT_MUTATION,
            variables: {
                orderCode,
                couponIds,
                orderParams
            }
        }
    );

    updateOrder(promise, ['removeDiscountFromOrder']);

    return promise;
}

export const setDeliveryPickupTime = (orderCode, selectedDeliveryTime) => {
    const promise = client.mutate({
        mutation: SET_DELIVERY_TIME_MUTATION,
        fetchPolicy: 'no-cache',
        variables: {
            orderCode,
            deliveryTime: selectedDeliveryTime
        }
    });

    return promise;
};

/**
 * @param {Object} data {
 *     firstName: String,
 *     lastName: String,
 *     phone: String,
 *     message: String
 * }
 */
export const addGiftRecipient = async (orderCode, data) => {
    const promise = client.mutate({
        mutation: ADD_GIFT_RECIPIENT_MUTATION,
        variables: {
            orderCode,
            data
        }
    });

    updateOrder(promise, ['addGiftRecipientDetailsToOrder']);

    return promise;
};

/**
 * @param {Object} data {
 *     firstName: String,
 *     lastName: String,
 *     email: String,
 *     phone: String
 * }
 */
export const addSender = async (orderCode, data) => {
    const promise = client.mutate({
        mutation: CREATE_ORDER_CONTACT_MUTATION,
        fetchPolicy: 'no-cache',
        variables: {
            orderCode,
            data
        }
    });

    updateOrder(promise, ['createCustomerContactOnOrder']);

    return promise;
};

/**
 * @param {Object} data {
 *     firstName: String,
 *     lastName: String,
 *     email: String,
 *     phone: String
 * }
 */
export const updateSender = (orderId, data) => {
    const promise = client.mutate({
        mutation: EDIT_ORDER_CONTACT_DETAILS_MUTATION,
        variables: {
            orderId,
            data
        }
    });

    updateOrder(promise, ['updateCustomerContact']);

    return promise;
};

/**
 * @param {Object} data {
 *     firstName: String,
 *     lastName: String,
 *     email: String,
 *     phone: String
 * }
 */
export const removeSender = orderCode => {
    const promise = client.mutate({
        mutation: REMOVE_CONTACT_DETAILS_MUTATION,
        variables: {
            orderCode
        }
    });

    updateOrder(promise, ['updateOrder']);

    return promise;
};

/**
 * @param {Object} data {
 *     orderProductId: Int,
 *     product: Int,
 *     quantity: Int,
 *     productOptions: [
 *         Int
 *     ]
 * }
 */
export const addUpdateOrderProduct = (orderCode, data) => {
    const promise = client.mutate({
        mutation: UPDATE_PRODUCT_ORDER_MUTATION,
        variables: {
            orderCode,
            data
        }
    });

    updateOrder(promise, ['updateProductOrder']);

    return promise;
};

export const updateOrderGiftProduct = (orderCode, data) => {
    const promise = client.mutate({
        mutation: UPDATE_GIFT_PRODUCT_TO_ORDER_MUTATION,
        variables: {
            orderCode,
            data
        }
    });

    updateOrder(promise, ['updateOrderGiftProduct']);

    return promise;
};

/**
 * @param {Object} order {
 *     code: String,
 *     useCookieDough: Boolean
 * }
 */
export const updateCookieDough = order => {
    const { useCookieDough, code } = order;

    const promise = client.mutate({
        mutation: ORDER_USE_COOKIE_DOUGH_MUTATION,
        variables: {
            orderCode: code,
            useCookieDough: !useCookieDough
        }
    });

    updateOrder(promise, ['updateOrder']);

    return promise;
};

export const setTip = async (orderCode, tip) => {
    const promise = client.mutate({
        mutation: SET_ORDER_TIP_MUTATION,
        fetchPolicy: 'no-cache',
        variables: {
            orderCode,
            tip
        }
    });

    await updateOrder(promise, ['updateOrder']);

    return promise;
};

/**
 * Completes Order
 *
 * @param {string} orderCode
 * @param {Int|String} paymentCredential
 * @param {string} orderOtp
 * @param {string} address2
 * @param {boolean} alertSenderPhone
 * @param {*[]} alertRecipientPhone
 * @param {string[]}subscriptionIds
 */
export const completeOrder = (orderCode, paymentCredential, orderOtp, address2, alertSenderPhone = false, alertRecipientPhone = false, subscriptionIds = []) => {
    const variables = {
        orderCode,
        paymentCredential,
        alertSenderPhone,
        alertRecipientPhone,
        subscriptionIds
    };

    if (orderOtp !== null) {
        variables.orderOtp = orderOtp
    }
    if (address2 !== null) {
        variables.address2 = address2
    }
    const promise = client.mutate({
        mutation: COMPLETE_ORDER_MUTATION,
        variables
    });

    return promise;
};

/**
 * Completes Order
 *
 * @param {string} orderCode
 * @param {string} deliveryTime
 * @param {Object} contact
 * @param {Object} recipient
 * @param {String} comment
 * @param {Int|String} paymentCredential
 * @param {string} orderOtp
 * @param {string} address2
 * @param {boolean} alertSenderPhone
 * @param {*[]} alertRecipientPhone
 * @param {string[]}subscriptionIds
 * @param {Number} tip
 * @param {boolean} includeSpoon
 */
export const completeOrderV2 = (orderCode, deliveryTime, contact, recipient, comment, paymentCredential, orderOtp, address2, alertSenderPhone = false, alertRecipientPhone = false,
    subscriptionIds = [], tip = 0, includeSpoon, isContactlessDelivery, locationLabel) => {
    const variables = {
        orderCode,
        deliveryTime,
        contact,
        paymentCredential,
        alertSenderPhone,
        alertRecipientPhone,
        subscriptionIds,
        recipient,
        comment,
        tip,
        includeSpoon,
        isContactlessDelivery,
        locationLabel,
    };

    if (orderOtp !== null) {
        variables.orderOtp = orderOtp
    }
    if (address2 !== null) {
        variables.address2 = address2
    }
    const promise = client.mutate({
        mutation: COMPLETE_ORDER_MUTATION_V2,
        variables
    });

    return promise;
};

/**
 * Genereate New OTP for Order.
 *
 * @param {string} orderCode
 */
export const generateOrderOtp = (orderCode) => {
    const variables = {
        orderCode
    };

    const promise = client.mutate({
        mutation: GENERATE_ORDER_OTP_MUTATION,
        variables
    });

    return promise;
};

/**
 *
 * @param {string} orderCode
 * @param {Int|string} paymentMethodId
 */
export const setPaymentMethod = (orderCode, paymentMethodId) => {
    const promise = client.mutate({
        mutation: SET_PAYMENT_METHOD_MUTATION,
        variables: {
            orderCode,
            paymentMethodId
        }
    });

    updateOrder(promise, ['updateOrder']);

    return promise;
};

/**
 * @param orderCode
 * @param {Object} data {
 *     product: ID
 *     quantity: int
 * }
 */
export const addProductToOrder = (orderCode, data, analytics) => {
    const previousOrder = store.getState().order;
    const promise = client.mutate({
        mutation: ADD_PRODUCT_TO_ORDER_MUTATION,
        variables: {
            orderCode,
            data,
        },
    });

    updateOrder(promise, ['addProductToOrderV2'], true);

    promise
        .then((result) => {
            try {
                let { data: responseData } = result;
                ['addProductToOrderV2'].forEach((path) => {
                    responseData = responseData[path];
                });
                const newOrder = { ...previousOrder, ...responseData };
                amplitudeAnalytics.afterSuccessfulAddProduct({
                    previousOrder,
                    order: newOrder,
                    addPayload: data,
                    analytics: analytics || {},
                    reduxState: store.getState(),
                });
            } catch {
                /* analytics must not affect cart */
            }
        })
        .catch(() => {});

    return promise;
};

/**
 * @typedef {Object} AddToCartData
 * @property {string} product
 * @property {number} quantity
 *
 * @param {string} orderCode
 * @param {AddToCartData|undefined} data
 * @param {Function} onOrderResolved
 * @returns
 */
export const addProductToOrderV2 = (orderCode, data, onOrderResolved, analytics) => {
    // generate a unique id for the cart transaction. this is used to prevent
    // race conditions when the addProductToOrderV2 mutation is called
    // multiple times in a row.
    const previousOrder = store.getState().order;
    const cartTransactionId = uuid();
    store.dispatch(setCartTransactionId(cartTransactionId));

    // Dispatch optimistic update to immediately update UI
    if (data) {
        const optimisticQty = Number(data.quantity);
        store.dispatch(
            addOptimisticItem(
                data.product,
                Number.isFinite(optimisticQty) && optimisticQty > 0 ? optimisticQty : 1,
            ),
        );
    }

    const promise = client.mutate({
        mutation: ADD_PRODUCT_TO_ORDER_MUTATION_V2,
        variables: {
            orderCode,
            data
        }
    });

    promise.then(() => {
        const orderPromise = getOrder(orderCode, undefined, ORDER_QUERY_V2, {
            abortController: new AbortController(),
            debug: true,
        });

        // Clear optimistic updates when actual data arrives
        orderPromise.then(response => {
            store.dispatch(clearOptimisticItems());
            updateOrder(orderPromise, ["order"], true);

            // Reset transaction ID if it matches
            if (store.getState()?.user?.cartTransactionId === cartTransactionId) {
              store.dispatch(setCartTransactionId(null));
            }
            try {
                const freshOrder = response?.data?.order;
                if (freshOrder) {
                    amplitudeAnalytics.afterSuccessfulAddProduct({
                        previousOrder,
                        order: freshOrder,
                        addPayload: data,
                        analytics: analytics || {},
                        reduxState: store.getState(),
                    });
                }
            } catch {
                /* analytics must not affect cart */
            }
            if (onOrderResolved) {
              onOrderResolved(response);
            }
        }).catch(() => {
            // Clear optimistic updates on error too
            store.dispatch(clearOptimisticItems());
        });
    }).catch(() => {
        // Clear optimistic updates on error
        store.dispatch(clearOptimisticItems());
    });

    return promise;
}

/**
 * @param {Object} data {
 *     productId: int
 *     type: string
 * }
 */
 export const addProductToUpsellFeatured = (orderCode, data) => {
    const promise = client.mutate({
        mutation: ADD_PRODUCT_TO_UPSELL_FEATURED_MUTATION,
        variables: {
            orderCode,
            data
        }
    });

   return promise;
};

export const addGiftProductToOrder = (orderCode, data, analytics) => {
    const previousOrder = store.getState().order;
    const promise = client.mutate({
        mutation: ADD_GIFT_PRODUCT_TO_ORDER_MUTATION,
        variables: {
            orderCode,
            data
        }
    });

    updateOrder(promise, ['addGiftProductToOrder']);

    promise
        .then((result) => {
            try {
                let { data: responseData } = result;
                ['addGiftProductToOrder'].forEach((path) => {
                    responseData = responseData[path];
                });
                const newOrder = { ...previousOrder, ...responseData };
                amplitudeAnalytics.afterSuccessfulAddProduct({
                    previousOrder,
                    order: newOrder,
                    addPayload: data,
                    analytics: analytics || {},
                    reduxState: store.getState(),
                });
            } catch {
                /* analytics must not affect cart */
            }
        })
        .catch(() => {});

    return promise;
};

/**
 * @param {Object} data {
 *     orderTypeId: Int!,
 *     shippingMethod: Int!,
 *     storeId: ID!,
 *     address: {
 *         address1: String!,
 *         city: String!,
 *         state: String!,
 *         postcode: String!,
 *         lat: Float!,
 *         lng: Float!
 *     }
 * }
 */
export const createCart = (data, { replaceOrder = false } = {}) => {
    const promise = client.mutate({
        mutation: CREATE_CART_MUTATION,
        variables: data
    });

    // Explicit fulfillment changes must replace (not merge) the previous order,
    // and finish loading the new cart before its first product is added.
    if (replaceOrder) {
        return promise.then(async result => {
            const response = await getOrderWithoutUpdate(result.data.createCart.code);
            if (!response.data?.order || response.errors?.length) {
                throw new Error('Unable to load the new cart. Please try again.');
            }
            store.dispatch(orderUpdated(response.data.order));
            try {
                amplitudeAnalytics.trackStartOrder(data, store.getState());
            } catch {
                /* analytics must not affect cart creation */
            }
            return result;
        });
    }

    promise
        .then((result) => {
            const orderCode = result?.data?.createCart?.code;
            if (orderCode) {
                // Fetch full order first so Start Order can read store name from order.store
                return getOrder(orderCode).then(() => {
                    try {
                        amplitudeAnalytics.trackStartOrder(data, store.getState());
                    } catch {
                        /* analytics must not affect cart creation */
                    }
                });
            }
            return undefined;
        })
        .catch(() => {});

    return promise;
}

export const getTracker = trackingID => {
    return client.query({
        query: ORDER_TRACKER_QUERY,
        fetchPolicy: 'no-cache',
        variables: {
            trackingID,
        }
    });
};

export const getConfirmedOrder = orderCode => {
    return client.query({
        query: ORDER_CONFIRMATION_QUERY,
        fetchPolicy: 'no-cache',
        variables: {
            orderCode
        }
    });
};

export const setShippingAddress = (orderCode, shippingAddressExternalId) => {
    const promise = client.mutate({
        fetchPolicy: 'no-cache',
        mutation: SET_ORDER_SHIPPING_ADDRESS,
        variables: {
            orderCode,
            shippingAddressExternalId
        }
    });

    updateOrder(promise, ['updateOrder']);

    return promise;
};

export const setShippingMethod = (orderCode, shippingMethodId) => {
    const promise = client.mutate({
        fetchPolicy: 'no-cache',
        mutation: SET_ORDER_SHIPPING_METHOD,
        variables: {
            orderCode,
            shippingMethodId
        }
    });

    updateOrder(promise, ['updateOrder']);

    return promise;
};

export const viewOrderDetails = orderCode => {
    const promise =  client.mutate({
        fetchPolicy: 'no-cache',
        mutation: VIEW_ORDER_DETAILS_QUERY,
        variables: {
            orderCode
        }
    });

    updateOrder(promise, ['order']);

    return promise;
};

export const updateOrderComment = async (orderCode, comment) => {
    const promise = client.mutate({
        fetchPolicy: 'no-cache',
        mutation: UPDATE_ORDER_COMMENT,
        variables: {
            orderCode,
            comment
        }
    });

    updateOrder(promise, ['updateOrder']);

    return promise;
}

export const getDriverLocation = orderId => {
    return client.query({
        query: DRIVER_LOCATION_QUERY,
        fetchPolicy: 'no-cache',
        variables: {
            orderId,
        }
    });
};
export const updateOrderIncludeSpoon = async (orderCode, includeSpoon) => {
    const promise = client.mutate({
        fetchPolicy: 'no-cache',
        mutation: UPDATE_ORDER_INCLUDESPOON,
        variables: {
            orderCode,
            includeSpoon
        }
    });

    updateOrder(promise, ['updateOrder']);

    return promise;
}

export const updateOrderDetails = async (orderCode, updateOrderDetailsData) => {
    const promise = client.mutate({
        fetchPolicy: 'no-cache',
        mutation: UPDATE_ORDER_DETAILS,
        variables: {
            orderCode,
            data: {
                ...updateOrderDetailsData
            }
        }
    });

    return promise;
}

export const orderEvent = async (eventName, orderCode, eventProperties) => {
    const promise = client.mutate({
        fetchPolicy: 'no-cache',
        mutation: ORDER_EVENT,
        variables: {
            eventName,
            orderCode,
            eventProperties
        }
    });

    // updateOrder(promise, ['brazeCheckoutStarted']);

    return promise;
}

export const getCancelReasons = async () => {
    const result = await client.query({
        query: GET_CANCEL_REASONS,
        fetchPolicy: 'no-cache'
    });
    return result?.data?.customerCancelRefundReasons || [];
};

export const cancelOrderByReason = async (orderCode, reasonId) => {
    return client.mutate({
        mutation: ORDER_CANCEL,
        fetchPolicy: 'no-cache',
        variables: {
            orderCode,
            reasonId
        }
    });
};

/**
 * Add or remove product options (like gift box sleeves) from an order product
 * @param {string} orderCode - The order code
 * @param {number} productId - The product ID
 * @param {number} orderProductId - The order product ID
 * @param {Array} options - Array of ProductOptionInput objects: [{ optionId: number, remove?: boolean }]
 * @returns {Promise} - Promise resolving to the updated order
 */
export const addOptionsToOrderProduct = async (orderCode, productId, orderProductId, options) => {
    const promise = client.mutate({
        mutation: ADD_OPTIONS_TO_ORDER_PRODUCT_MUTATION,
        variables: {
            orderCode,
            productId,
            orderProductId,
            options
        }
    });

    updateOrder(promise, ['addOptionsToOrderProduct']);

    return promise;
};

/**
 * Update dropoff location for delivery orders
 * @param {string} orderCode - The order code
 * @param {Object} coords - The dropoff coordinates {lat: Float, lng: Float}
 * @returns {Promise} - Promise resolving to the updated order
 */
export const updateDropoffLocation = async (orderCode, coords) => {
    const promise = updateOrderDetails(orderCode, {
        dropoffLocation: coords
    });

    return promise;
};
